import { z } from 'zod';
import { cronFormSchemaObject } from './cronTypes';
import { isCronFieldValid } from './isCronFieldValid';

export const cronWildcard = '*';

const numberTokenRegex = /^[0-9]+$/;

export const isWildcardValue = (value: string | undefined): boolean => {
  if (value === undefined) {
    return false;
  }

  return value.trim() === cronWildcard;
};

export const isNumberWithinRange = (
  token: string | undefined,
  min: number,
  max: number,
): boolean => {
  if (token === undefined) {
    return false;
  }

  const normalizedValue = token.trim();
  if (normalizedValue === '') {
    return false;
  }

  if (!numberTokenRegex.test(token)) {
    return false;
  }

  const numericValue = Number(token);
  if (!Number.isInteger(numericValue)) {
    return false;
  }

  if (numericValue < min) {
    return false;
  }

  return numericValue <= max;
};

const isCommaSeparatedNumberListWithinRange = (
  value: string | undefined,
  min: number,
  max: number,
): boolean => {
  if (value === undefined) {
    return false;
  }

  const normalizedValue = value.trim();
  if (normalizedValue === '') {
    return false;
  }

  const parts = normalizedValue.split(',');
  if (parts.length === 0) {
    return false;
  }

  for (const part of parts) {
    if (!isNumberWithinRange(part, min, max)) {
      return false;
    }
  }

  return true;
};

const isNumberOrCommaSeparatedListWithinRange = (
  value: string | undefined,
  min: number,
  max: number,
): boolean => {
  if (value === undefined) {
    return false;
  }

  if (value.includes(',')) {
    return isCommaSeparatedNumberListWithinRange(value, min, max);
  }

  return isNumberWithinRange(value, min, max);
};

export const isWildcardOrNumberOrCommaSeparatedListWithinRange = (
  value: string | undefined,
  min: number,
  max: number,
): boolean => {
  if (isWildcardValue(value)) {
    return true;
  }

  return isNumberOrCommaSeparatedListWithinRange(value, min, max);
};

export const isNumberOrCommaSeparatedListWithinRangePublic =
  isNumberOrCommaSeparatedListWithinRange;

const addValidationIssue = (
  context: z.RefinementCtx,
  path: (string | number)[],
  message = '',
): void => {
  context.addIssue({
    code: z.ZodIssueCode.custom,
    message,
    path,
  });
};

const normalizeArrayField = (value: string | string[]): string[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return value.split(',').filter((item) => item.trim() !== '');
};

const normalizeStringField = (value: string | string[]): string => {
  if (Array.isArray(value)) {
    return value.join(',');
  }
  return value;
};

const validateCronField = (
  value: string | string[],
  fieldType: 'minute' | 'hour' | 'dayOfMonth' | 'month' | 'dayOfWeek',
  context: z.RefinementCtx,
  path: (string | number)[],
): void => {
  const normalizedValue = normalizeStringField(value);
  if (!isCronFieldValid(normalizedValue, fieldType)) {
    addValidationIssue(context, path);
  }
};

const validateCustomCronFields = (
  data: z.infer<typeof cronFormSchemaObject>,
  context: z.RefinementCtx,
): void => {
  validateCronField(data.minute, 'minute', context, ['minute']);
  validateCronField(data.hour, 'hour', context, ['hour']);
  validateCronField(data.days, 'dayOfMonth', context, ['days']);
  validateCronField(data.months, 'month', context, ['months']);
  validateCronField(data.weekDays, 'dayOfWeek', context, ['weekDays']);
};

const isValidIntegerInRange = (
  value: number,
  min: number,
  max: number,
): boolean => {
  return (
    !Number.isNaN(value) &&
    Number.isInteger(value) &&
    value >= min &&
    value <= max
  );
};

const validateNumericField = (
  value: string,
  min: number,
  max: number,
  context: z.RefinementCtx,
  path: (string | number)[],
): void => {
  const numericValue = Number(value);
  if (!isValidIntegerInRange(numericValue, min, max)) {
    addValidationIssue(context, path);
  }
};

const validateEveryModeNumericFields = (
  data: z.infer<typeof cronFormSchemaObject>,
  context: z.RefinementCtx,
): void => {
  validateNumericField(data.minute, 0, 59, context, ['minute']);
  validateNumericField(data.hour, 0, 23, context, ['hour']);
};

const validateArrayFieldNotEmpty = (
  value: string | string[],
  context: z.RefinementCtx,
  path: (string | number)[],
  errorMessage: string,
): void => {
  const array = normalizeArrayField(value);
  if (array.length === 0) {
    addValidationIssue(context, path, errorMessage);
  }
};

const validateEveryModeArrayFields = (
  data: z.infer<typeof cronFormSchemaObject>,
  context: z.RefinementCtx,
): void => {
  if (data.every === 'year') {
    validateArrayFieldNotEmpty(
      data.months,
      context,
      ['months'],
      'Choose at least one month',
    );
  }

  if (data.every === 'year' || data.every === 'month') {
    validateArrayFieldNotEmpty(
      data.days,
      context,
      ['days'],
      'Choose at least one day',
    );
  }

  if (data.every === 'week') {
    validateArrayFieldNotEmpty(
      data.weekDays,
      context,
      ['weekDays'],
      'Choose at least one week day',
    );
  }
};

const validateEveryModeFields = (
  data: z.infer<typeof cronFormSchemaObject>,
  context: z.RefinementCtx,
): void => {
  validateEveryModeNumericFields(data, context);
  validateEveryModeArrayFields(data, context);
};

export const cronFormSchema = cronFormSchemaObject.superRefine(
  (data, context) => {
    if (data.every === 'custom') {
      validateCustomCronFields(data, context);
    } else {
      validateEveryModeFields(data, context);
    }
  },
);
